import { FC } from 'react'
import { Video } from 'lucide-react'
import MetaScoreSummary from 'pages/recap/MetaScoreSummary'
import {
  CriterionAnalysis,
  CriterionEvaluation,
  VideoAIAnalysis
} from '@facesignai/api'
import startCase from 'lodash/startCase'
import map from 'lodash/map'
import ReasoningLoader from 'pages/recap/ReasoningLoader'
import { DBT } from 'types/internal'

type Props = {
  videoAIAnalysis?: VideoAIAnalysis
  user?: DBT.VegasUserT
  sessionId: string
}

// const videoSubsections = [
//   { key: 'faceAuthenticity', title: 'Face Authenticity' },
//   { key: 'behavioralAnomalies', title: 'Behavioral Anomalies' },
//   { key: 'eyeAndHeadMovement', title: 'Eye & Head Movement' },
//   { key: 'facialExpressionAndMovement', title: 'Facial Expression & Movement' },
//   { key: 'lipSyncAccuracy', title: 'Lip Sync Accuracy' },
//   { key: 'environmentalConsistency', title: 'Environmental Consistency' },
//   { key: 'interactionConfidence', title: 'Interaction Confidence' },
//   {
//     key: 'presenceOfSuspiciousObjectsOrPeople',
//     title: 'Suspicious Objects or People',
//     statementsKey: 'suspiciousObjects'
//   },
//   {
//     key: 'useOfExternalDevices',
//     title: 'External Devices',
//     statementsKey: 'externalDevices'
//   }
// ]

const VideoAnalysis: FC<Props> = ({ videoAIAnalysis, user, sessionId }) => {
  const renderVideoSubsection = (metric: CriterionAnalysis) => {
    const borderColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'border-l-green-500'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'border-l-yellow-500'
          : 'border-l-red-500'

    const scoreColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'text-green-700'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'text-yellow-700'
          : 'text-red-700'

    const bgColor =
      metric.evaluation === CriterionEvaluation.normal
        ? 'bg-green-50'
        : metric.evaluation === CriterionEvaluation.suspicious
          ? 'bg-yellow-50'
          : 'bg-red-50'

    return (
      <div
        className={`border-l-4 ${borderColor} ${bgColor} rounded-r-lg p-4 relative animate-fadeIn`}
      >
        <div className='flex items-start justify-between mb-2'>
          <div className='flex-1'>
            <h4 className='font-semibold text-gray-900 mb-1'>
              {startCase(metric.criterion)}
            </h4>
            <span
              className={`inline-block text-base font-bold uppercase ${scoreColor} px-2 py-0.5 rounded`}
              style={{
                backgroundColor:
                  metric.evaluation === CriterionEvaluation.normal
                    ? 'rgba(34, 197, 94, 0.1)'
                    : metric.evaluation === CriterionEvaluation.suspicious
                      ? 'rgba(234, 179, 8, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)'
              }}
            >
              {metric.evaluation === CriterionEvaluation.normal
                ? '✓ Normal'
                : metric.evaluation === CriterionEvaluation.suspicious
                  ? '⚠ Suspicious'
                  : '⚠ High Risk'}
            </span>
          </div>
        </div>
        <p className='text-sm text-gray-700 pr-12'>{metric.observation}</p>
        <span className='absolute bottom-2 right-3 text-xs text-gray-400'>
          {Math.round(metric.confidence)}% conf
        </span>
      </div>
    )
  }

  const renderFacialRecognition = () => {
    if (!user) return null

    const isNewUser = user.createdBySessionId === sessionId
    const company = user.company as string | undefined
    const jobTitle = user.jobTitle as string | undefined

    const borderColor = isNewUser ? 'border-l-blue-500' : 'border-l-green-500'
    const bgColor = isNewUser ? 'bg-blue-50' : 'bg-green-50'
    const labelColor = isNewUser ? 'text-blue-700' : 'text-green-700'
    const labelBg = isNewUser
      ? 'rgba(59, 130, 246, 0.1)'
      : 'rgba(34, 197, 94, 0.1)'

    const userDetails = isNewUser
      ? [user.name, user.email].filter(Boolean).join('; ')
      : [user.name, jobTitle, company, user.email].filter(Boolean).join(', ')

    return (
      <div
        className={`border-l-4 ${borderColor} ${bgColor} rounded-r-lg p-4 relative animate-fadeIn`}
      >
        <div className='flex items-start justify-between mb-2'>
          <div className='flex-1'>
            <h4 className='font-semibold text-gray-900 mb-1'>
              Facial Recognition
            </h4>
            <span
              className={`inline-block text-base font-bold uppercase ${labelColor} px-2 py-0.5 rounded`}
              style={{ backgroundColor: labelBg }}
            >
              {isNewUser ? '+ New User' : '✓ Recognized'}
            </span>
          </div>
        </div>
        <p className='text-sm text-gray-700'>{userDetails}</p>
      </div>
    )
  }

  if (!videoAIAnalysis) {
    const statements = [
      'Initializing video frame analysis...',
      'Processing facial geometry...',
      'Analyzing temporal consistency...',
      'Detecting potential manipulation artifacts...',
      'Evaluating video authenticity signals...',
      'Continuing deep frame analysis...',
      'Processing additional video segments...',
      'Analyzing cross-frame consistency...',
      'Evaluating temporal authenticity markers...',
      'Detecting frame-level anomalies...',
      'Checking for temporal manipulation...',
      'Analyzing compression artifacts...',
      'Evaluating lighting transitions...',
      'Processing motion blur patterns...',
      'Analyzing shadow consistency...',
      'Checking reflection authenticity...',
      'Evaluating color grading consistency...',
      'Analyzing audio-visual synchronization...',
      'Processing background motion...',
      'Finalizing comprehensive video assessment...'
    ]

    return (
      <div className='flex w-full flex-col gap-2 mt-4'>
        <ReasoningLoader
          statements={statements}
          intervalMs={1500}
          minStatements={5}
          shouldComplete={!!videoAIAnalysis}
        />
      </div>
    )
  } else {
    return (
      <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
        <div className='flex items-center gap-2 mb-4'>
          <Video className='text-blue-500' size={20} />
          <h2 className='text-lg font-semibold text-gray-900'>
            Detected Signals
          </h2>
        </div>
        <MetaScoreSummary videoAIAnalysis={videoAIAnalysis} />
        <div className='space-y-3 mt-4'>
          {renderFacialRecognition()}
          {map(videoAIAnalysis, (v) => (
            <div key={v.criterion}>{renderVideoSubsection(v)}</div>
          ))}
        </div>
      </div>
    )
  }
}

export default VideoAnalysis
